import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken, getStoredUser } from '../utils/authSession';
import {
  THEME_PRESETS,
  DEFAULT_THEME_ID,
  FESTIVE_EFFECT_OPTIONS,
  buildThemeFromSettings,
  applyThemeColors,
  notifyThemeUpdated,
  getDefaultPopupForTheme,
  isFestiveTheme
} from '../utils/siteTheme';

const DEFAULT_COLORS = {
  active_theme: DEFAULT_THEME_ID,
  announcement_bar_color: '#003d82',
  announcement_bar_color_end: '#0056b3',
  brand_primary: '#003d82',
  brand_primary_light: '#0056b3',
  footer_bg: '#2c3e50',
  page_header: '#003d82',
  surface_tint: '#eef4fa',
  popup_message: '',
  popup_enabled: '0',
  festive_effect: 'none'
};

function AdminAnnouncements() {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isAdminUser = user?.role === 'admin';

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingColors, setSavingColors] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    is_active: true,
    display_order: 1
  });
  const [colors, setColors] = useState(DEFAULT_COLORS);

  const preview = useMemo(() => buildThemeFromSettings(colors), [colors]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAll();
  }, [navigate]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const response = await axios.get('/api/announcements', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data.announcements || []);
      setColors({
        ...DEFAULT_COLORS,
        ...(response.data.settings || {}),
        popup_message: response.data.settings?.popup_message ?? '',
        popup_enabled:
          response.data.settings?.popup_enabled === '1' ||
          response.data.settings?.popup_enabled === 1 ||
          response.data.settings?.popup_enabled === true
            ? '1'
            : '0',
        festive_effect: response.data.settings?.festive_effect || 'none'
      });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
        return;
      }
      console.error('Error loading announcements:', error);
      const apiError = error.response?.data?.error;
      const status = error.response?.status;
      if (status === 403) {
        alert(apiError || 'Access denied. Admin or Content Editor role required.');
      } else if (status === 404) {
        alert(
          'Announcements API not found (404). Upload updated server/server.js and server/routes/announcements.js, then Restart the Node app.'
        );
      } else {
        alert(apiError || `Failed to load announcements${status ? ` (${status})` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setFormData({
      message: '',
      is_active: true,
      display_order: (announcements.length || 0) + 1
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      message: item.message || '',
      is_active: Boolean(item.is_active),
      display_order: item.display_order || 1
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const token = getAccessToken();
      const payload = {
        message: formData.message.trim(),
        is_active: formData.is_active,
        display_order: Number(formData.display_order) || 1
      };

      if (editing) {
        await axios.put(`/api/announcements/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Announcement updated successfully!');
      } else {
        await axios.post('/api/announcements', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Announcement created successfully!');
      }

      setShowModal(false);
      fetchAll();
      notifyThemeUpdated();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save announcement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const token = getAccessToken();
      await axios.delete(`/api/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Announcement deleted successfully!');
      fetchAll();
      notifyThemeUpdated();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete announcement');
    }
  };

  const toggleActive = async (item) => {
    try {
      const token = getAccessToken();
      await axios.put(
        `/api/announcements/${item.id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
      notifyThemeUpdated();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update announcement');
    }
  };

  const saveColors = async () => {
    if (!isAdminUser) {
      alert('Only admins can change the website theme.');
      return;
    }
    setSavingColors(true);
    try {
      const token = getAccessToken();
      await axios.put(
        '/api/announcements/settings/colors',
        {
          active_theme: colors.active_theme || 'custom',
          announcement_bar_color: colors.announcement_bar_color,
          announcement_bar_color_end: colors.announcement_bar_color_end,
          brand_primary: colors.brand_primary,
          brand_primary_light: colors.brand_primary_light,
          footer_bg: colors.footer_bg,
          page_header: colors.page_header,
          surface_tint: colors.surface_tint,
          popup_message: colors.popup_message || '',
          popup_enabled: colors.popup_enabled === '1' || colors.popup_enabled === true,
          festive_effect: colors.festive_effect || 'none'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      applyThemeColors(colors);
      alert('Website theme saved. The public site will use this look.');
      notifyThemeUpdated();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update theme');
    } finally {
      setSavingColors(false);
    }
  };

  const resetColors = async () => {
    if (!isAdminUser) return;
    if (!window.confirm('Reset the whole website to the default hospital blue theme?')) return;
    setSavingColors(true);
    try {
      const token = getAccessToken();
      const response = await axios.post(
        '/api/announcements/settings/reset-colors',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const next = {
        ...DEFAULT_COLORS,
        ...(response.data.settings || {})
      };
      setColors(next);
      applyThemeColors(next);
      alert('Theme reset to default hospital blue!');
      notifyThemeUpdated();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to reset theme');
    } finally {
      setSavingColors(false);
    }
  };

  const applyPreset = (preset) => {
    const festive = isFestiveTheme(preset.id) || preset.effect !== 'none';
    const nextEffect = preset.id === 'custom' ? colors.festive_effect || 'none' : preset.effect || 'none';
    setColors((prev) => ({
      active_theme: preset.id,
      announcement_bar_color:
        preset.id === 'custom' ? prev.announcement_bar_color : preset.announcementBar,
      announcement_bar_color_end:
        preset.id === 'custom' ? prev.announcement_bar_color_end : preset.announcementBarEnd,
      brand_primary: preset.id === 'custom' ? prev.brand_primary : preset.brandPrimary,
      brand_primary_light:
        preset.id === 'custom' ? prev.brand_primary_light : preset.brandPrimaryLight,
      footer_bg: preset.id === 'custom' ? prev.footer_bg : preset.footerBg,
      page_header: preset.id === 'custom' ? prev.page_header : preset.pageHeader,
      surface_tint: preset.id === 'custom' ? prev.surface_tint : preset.surfaceTint,
      popup_message:
        preset.id === 'custom'
          ? prev.popup_message
          : festive
            ? preset.defaultPopup || getDefaultPopupForTheme(preset.id) || ''
            : '',
      popup_enabled:
        preset.id === 'custom' ? prev.popup_enabled : festive ? '1' : '0',
      festive_effect: nextEffect
    }));
  };

  const updateColor = (key, value) => {
    setColors((prev) => ({
      ...prev,
      [key]: value,
      active_theme: 'custom'
    }));
  };

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1 className="h3 mb-1">Announcements & Site Theme</h1>
            <p className="text-muted mb-0">
              Switch the website for Ghana holidays and special days. Each festive theme includes
              animations and an optional popup visitors can close with ×.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-lg me-1" />
            Add Announcement
          </button>
        </div>

        {isAdminUser && (
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>Full website theme</strong>
              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={resetColors}
                  disabled={savingColors}
                >
                  Reset to default
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={saveColors}
                  disabled={savingColors}
                >
                  {savingColors ? 'Saving...' : 'Apply theme to website'}
                </button>
              </div>
            </div>
            <div className="card-body">
              <p className="mb-2 fw-semibold">Ghana holidays & special days (one click)</p>
              <p className="text-muted small mb-3">
                Pick a holiday preset, or choose <strong>Custom theme</strong> and set your own
                colours. Independence Day uses a forest-green palette, the Black Star Gate image, and
                raining Ghana flags. Constitution Day uses gold with coat-of-arms accents. Snow is
                only for Christmas and New Year’s Eve.
              </p>
              <div className="row g-3 mb-4">
                {THEME_PRESETS.map((preset) => {
                  const selected = colors.active_theme === preset.id;
                  return (
                    <div key={preset.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                      <button
                        type="button"
                        className={`w-100 text-start border rounded-3 p-3 h-100 tgh-theme-preset-btn ${
                          selected ? 'border-primary shadow-sm' : 'border-secondary-subtle'
                        }`}
                        style={{ background: '#fff', minHeight: 120 }}
                        onClick={() => applyPreset(preset)}
                      >
                        <div
                          className="rounded mb-2"
                          style={{
                            height: 42,
                            background: `linear-gradient(135deg, ${preset.announcementBar}, ${preset.announcementBarEnd})`
                          }}
                        />
                        <div className="fw-semibold">{preset.name}</div>
                        <small className="text-muted d-block">{preset.description}</small>
                        {selected && (
                          <span className="badge bg-primary mt-2">Selected</span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="border rounded-3 p-3 mb-4 bg-light">
                <p className="mb-2 fw-semibold">Holiday popup message</p>
                <p className="text-muted small mb-3">
                  Shown once per visit on the public site when enabled. Visitors can close it with the
                  × button.
                </p>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="popupEnabled"
                    checked={colors.popup_enabled === '1' || colors.popup_enabled === true}
                    onChange={(e) =>
                      setColors((prev) => ({
                        ...prev,
                        popup_enabled: e.target.checked ? '1' : '0'
                      }))
                    }
                  />
                  <label className="form-check-label" htmlFor="popupEnabled">
                    Show popup alert on the public website
                  </label>
                </div>
                <label className="form-label" htmlFor="popupMessage">
                  Popup text
                </label>
                <textarea
                  id="popupMessage"
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  placeholder="Type the holiday greeting visitors will see…"
                  value={colors.popup_message || ''}
                  onChange={(e) =>
                    setColors((prev) => ({
                      ...prev,
                      popup_message: e.target.value
                    }))
                  }
                />
                <small className="text-muted">
                  {(colors.popup_message || '').length}/500 characters
                </small>
              </div>

              <div className="border rounded-3 p-3 mb-4">
                <p className="mb-2 fw-semibold">Animation for this theme</p>
                <p className="text-muted small mb-2">
                  Holiday presets set this automatically. For a custom theme, choose what visitors see.
                </p>
                <label className="form-label" htmlFor="festiveEffect">
                  Public-site animation
                </label>
                <select
                  id="festiveEffect"
                  className="form-select"
                  value={colors.festive_effect || 'none'}
                  onChange={(e) =>
                    setColors((prev) => ({
                      ...prev,
                      festive_effect: e.target.value,
                      active_theme: 'custom'
                    }))
                  }
                >
                  {FESTIVE_EFFECT_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <p className="mb-2 fw-semibold">Live preview (contrast auto-checked)</p>
              <div className="row g-3 mb-4">
                <div className="col-12 col-lg-6">
                  <div
                    className="rounded px-3 py-2 mb-2"
                    style={{
                      background: `linear-gradient(135deg, ${preview.announcementBar}, ${preview.announcementBarEnd})`,
                      color: preview.onAnnouncement
                    }}
                  >
                    Announcement bar text stays readable
                  </div>
                  <div
                    className="rounded px-3 py-3 mb-2"
                    style={{
                      background: `linear-gradient(135deg, ${preview.pageHeader}, ${preview.brandPrimaryLight})`,
                      color: preview.onPageHeader
                    }}
                  >
                    <strong>Page header preview</strong>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    style={{
                      background: preview.brandPrimary,
                      color: preview.onPrimary,
                      borderColor: preview.brandPrimary
                    }}
                  >
                    Primary button
                  </button>
                </div>
                <div className="col-12 col-lg-6">
                  <div
                    className="rounded p-3 h-100"
                    style={{
                      background: preview.footerBg,
                      color: preview.onFooter,
                      minHeight: 140
                    }}
                  >
                    <div className="fw-semibold mb-1">Footer preview</div>
                    <div style={{ color: preview.footerMuted }}>Muted link example</div>
                    <div className="small mt-2" style={{ color: preview.onFooter }}>
                      Surface tint: {preview.surfaceTint}
                    </div>
                  </div>
                </div>
              </div>

              <p className="mb-2 fw-semibold">Fine-tune colors (switches theme to Custom)</p>
              <div className="row g-3">
                {[
                  ['announcement_bar_color', 'Announcement start'],
                  ['announcement_bar_color_end', 'Announcement end'],
                  ['brand_primary', 'Buttons & links'],
                  ['brand_primary_light', 'Hover / light accent'],
                  ['page_header', 'Page headers'],
                  ['footer_bg', 'Footer background'],
                  ['surface_tint', 'Page background tint']
                ].map(([key, label]) => (
                  <div key={key} className="col-6 col-md-4 col-lg-3">
                    <label className="form-label">{label}</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      value={colors[key]}
                      onChange={(e) => updateColor(key, e.target.value)}
                    />
                    <small className="text-muted text-break">{colors[key]}</small>
                  </div>
                ))}
              </div>
              <p className="text-muted small mt-3 mb-0">
                Active theme id: <code>{colors.active_theme || 'custom'}</code>. Save to push this look to every public page.
              </p>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <strong>Sliding Announcements</strong>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : announcements.length === 0 ? (
              <p className="text-muted p-4 mb-0">No announcements yet. Add one for Christmas or any event.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th style={{ width: '220px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((item) => (
                      <tr key={item.id}>
                        <td>{item.display_order}</td>
                        <td>{item.message}</td>
                        <td>
                          <span className={`badge ${item.is_active ? 'bg-success' : 'bg-secondary'}`}>
                            {item.is_active ? 'Active' : 'Hidden'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openEdit(item)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => toggleActive(item)}
                            >
                              {item.is_active ? 'Hide' : 'Show'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(item.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">{editing ? 'Edit Announcement' : 'Add Announcement'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      maxLength={500}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="e.g. Merry Christmas from Tema General Hospital — clinics open on reduced hours"
                    />
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Display order</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        value={formData.display_order}
                        onChange={(e) =>
                          setFormData({ ...formData, display_order: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="announcementActive"
                          checked={formData.is_active}
                          onChange={(e) =>
                            setFormData({ ...formData, is_active: e.target.checked })
                          }
                        />
                        <label className="form-check-label" htmlFor="announcementActive">
                          Show on website
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminAnnouncements;
